//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQueries, useQuery } from '@tanstack/react-query';

import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import Cuid from '@models/Cuid';
import IItemMetadata from '@models/IItemMetadata';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isDefined, isUndefined } from '@utils/filterPredicates';

interface MultipleMediaMetadataQueryResult {
  data: Record<Cuid, IItemMetadata>;
  isError: boolean;
  isPending: boolean;
}

export const useMediaMetadataQuery = (cuid: Cuid | undefined) =>
  useQuery<IItemMetadata>(getMediaMetadataQueryOptions(cuid));

export const useMultipleMediaMetadataQuery = (
  cuids: Cuid[],
): MultipleMediaMetadataQueryResult => {
  const queries = useQueries({
    queries: cuids.map(getMediaMetadataQueryOptions),
  });

  const result: MultipleMediaMetadataQueryResult = {
    data: {},
    isError: queries.some((query) => query.isError),
    isPending: queries.some((query) => query.isPending),
  };

  return cuids.reduce((acc, cuid, index) => {
    const query = queries[index];

    if (isDefined(query?.data)) {
      acc.data[cuid] = query.data;
    }

    return acc;
  }, result);
};

const getMediaMetadataQueryOptions = (cuid: Cuid | undefined) => ({
  queryKey: [
    QueryKeyConstants.UniversalImageActions,
    QueryKeyConstants.UniversalImageActionsRights,
    QueryKeyConstants.UniversalImageActionsMetadata,
    cuid,
  ],
  queryFn: async ({ signal }: { signal?: AbortSignal }) => {
    if (isUndefined(cuid)) {
      throw new Error('Undefined cuid');
    }

    const partialCuid = Cuid.toPartial(cuid);
    const [metadata, statuses] = await Promise.all([
      getMediaItemMetadata(cuid, { signal }),
      getRightsRequestStatuses([partialCuid], { signal }),
    ]);
    const rightsRequestStatus = statuses.data[partialCuid];

    if (isDefined(rightsRequestStatus)) {
      return {
        ...metadata,
        rightsRequestStatus,
      };
    }

    return metadata;
  },
  enabled: isDefined(cuid) && cuid.length > 0,
  staleTime: FIVE_MINUTES_STALE_TIME,
});
